// src/components/Dashboard/CreateOrderModal.js
import React, { useState, useRef, useEffect, useCallback } from "react";
import { ToastContainer, toast } from "react-toastify";
import axios from "axios";
import {
  Search,
  Package,
  MapPin,
  Banknote,
  Landmark,
  QrCode,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  X,
  User,
  Minus,
  Plus,
  IndianRupee,
  Loader2,
  ShoppingCart,
} from "lucide-react";
import { postData1, suggestionSearch } from "../../../services";

// Backend payment_type choices (1-10)
const PAYMENT_METHODS = [
  {
    value: 9,
    label: "Cash on Delivery",
    hint: "Collect remaining amount on delivery",
    icon: Banknote,
  },
  {
    value: 2,
    label: "Bank Transfer",
    hint: "Remaining amount via bank transfer",
    icon: Landmark,
  },
  {
    value: 5,
    label: "QR Code / UPI",
    hint: "Share QR code to collect payment",
    icon: QrCode,
  },
];

// Backend payment_status choices (1-3)
const PAYMENT_STATUS = {
  1: { label: "Unpaid", badge: "bg-gray-100 text-gray-600" },
  2: { label: "Partially Paid", badge: "bg-amber-100 text-amber-700" },
  3: { label: "Paid", badge: "bg-emerald-100 text-emerald-700" },
};

const EMPTY_FORM = {
  productQuery: "",
  quantity: 1,
  unitPrice: "",
  discount: 0,
  tax: 0,
  advancePaid: "",
  paymentType: 9,
  deliveryAddress: null,
  // manual address fallback (for customers with no saved address)
  mName: "",
  mMobile: "",
  mAddress: "",
  mCity: "",
  mState: "",
  mZip: "",
};

const STEP_META = [
  { title: "Product", icon: Package },
  { title: "Address", icon: MapPin },
  { title: "Payment", icon: Banknote },
];

const CreateOrderModal = ({ userData, onOrderCreated }) => {
  const [showModal, setShowModal] = useState(false);
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productSuggestions, setProductSuggestions] = useState([]);
  const [showProductDropdown, setShowProductDropdown] = useState(false);
  const [addressList, setAddressList] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [loadingAddresses, setLoadingAddresses] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [savingAddress, setSavingAddress] = useState(false);
  const [manualAddress, setManualAddress] = useState(false);
  const [errors, setErrors] = useState({});

  const productDropdownRef = useRef(null);
  const productQueryRef = useRef("");

  // console.log("Creat", selectedProduct);
  // ---------- helpers ----------
  const setField = (key, value) =>
    setFormData((prev) => ({ ...prev, [key]: value }));

  const customerName =
    [userData?.profile?.first_name, userData?.profile?.last_name]
      .filter(Boolean)
      .join(" ") || "Customer";

  const productThumb = (product) =>
    product?.product_images?.[0]?.variants?.medium ||
    product?.product_images?.[0]?.p_image ||
    product?.product_images?.[0]?.product_images ||
    "";

  // ---------- product search (debounced) ----------
  const fetchProductSuggestions = useCallback(async (query) => {
    if (!query) {
      setProductSuggestions([]);
      return;
    }
    setLoadingProducts(true);
    try {
      const resp = await axios.get(
        `${process.env.REACT_APP_BASEURL}/product/improved-product/generic_search/`,
        {
          params: { search: query, isSearch: true },
          headers: {
            accept: "application/json",
            Authorization: `Token ${localStorage.getItem("adminAuth")}`,
          },
        }
      );
      const data = resp?.data;
      let suggestions = [];
      if (Array.isArray(data?.results)) suggestions = data.results;
      else if (Array.isArray(data)) suggestions = data;
      else if (Array.isArray(data?.products)) suggestions = data.products;

      if (!suggestions.length) {
        const result = await suggestionSearch(query);
        if (result && Array.isArray(result.suggestions)) {
          suggestions = result.suggestions;
        } else if (Array.isArray(result)) {
          suggestions = result;
        }
      }
      // Ignore stale responses (guard against out-of-order results)
      if (productQueryRef.current?.trim() !== query) return;
      setProductSuggestions(suggestions);
    } catch (error) {
      console.error("Error fetching product suggestions", error);
      setProductSuggestions([]);
    } finally {
      setLoadingProducts(false);
    }
  }, []);

  // Keep latest query in a ref so the debounced callback always reads current value
  useEffect(() => {
    productQueryRef.current = formData.productQuery;
  }, [formData.productQuery]);

  useEffect(() => {
    if (!showProductDropdown) return;
    const timeout = setTimeout(() => {
      if (formData.productQuery?.trim()) {
        fetchProductSuggestions(formData.productQuery.trim());
      } else {
        setProductSuggestions([]);
      }
    }, 300);
    return () => clearTimeout(timeout);
  }, [formData.productQuery, showProductDropdown, fetchProductSuggestions]);

  // click outside to close product dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        productDropdownRef.current &&
        !productDropdownRef.current.contains(event.target)
      ) {
        setShowProductDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // ---------- user saved addresses ----------
  const fetchAddresses = useCallback(async () => {
    if (!userData?.uid) return;
    setLoadingAddresses(true);
    try {
      const res = await postData1(
        "users/address_list/",
        { user: userData.uid },
        true,
        true
      );
      if (res?.status) {
        const list = Array.isArray(res?.data) ? res.data : [];
        setAddressList(list);
        const defaultAddr =
          list.find((a) => String(a?.is_default) === "1") || list[0] || null;
        setFormData((prev) => ({
          ...prev,
          deliveryAddress: prev.deliveryAddress || defaultAddr,
        }));
      } else {
        setAddressList([]);
      }
    } catch (error) {
      console.error("Error fetching addresses", error);
      setAddressList([]);
    } finally {
      setLoadingAddresses(false);
    }
  }, [userData?.uid]);

  const openModal = () => {
    setShowModal(true);
    setStep(1);
    setFormData(EMPTY_FORM);
    setSelectedProduct(null);
    setManualAddress(false);
    setSavingAddress(false);
    setErrors({});
    fetchAddresses();
  };

  const closeModal = () => {
    setShowModal(false);
    setStep(1);
    setFormData(EMPTY_FORM);
    setSelectedProduct(null);
    setManualAddress(false);
    setSavingAddress(false);
    setErrors({});
  };

  // ---------- derived pricing ----------
  // Round every monetary value to max 2 decimal places before submit
  const round2 = (n) => Math.round((n + 1e-8) * 100) / 100;

  const unitPrice = round2(parseFloat(formData.unitPrice) || 0);
  const quantity = parseInt(formData.quantity, 10) || 0;
  const discountPercent = parseFloat(formData.discount) || 0;
  const taxPercent = parseFloat(formData.tax) || 0;
  const subtotal = round2(unitPrice * quantity);
  const discountAmount = round2((subtotal * discountPercent) / 100);
  const taxAmount = round2((subtotal * taxPercent) / 100);
  const total = round2(Math.max(0, subtotal - discountAmount + taxAmount));
  const advancePaid = round2(parseFloat(formData.advancePaid) || 0);
  const remainingAmount = round2(Math.max(0, total - advancePaid));

  // Backend payment_status: 1=Unpaid, 2=Partial, 3=Paid
  const paymentStatus =
    advancePaid >= total && total > 0
      ? 3
      : advancePaid > 0
        ? 2
        : 1;

  const selectProduct = (product) => {
    setSelectedProduct(product);
    const price = product?.asking_price || product?.price || 0;
    setField("productQuery", product?.equip_name || product?.name || "");
    setField("unitPrice", price);
    setShowProductDropdown(false);
  };

  // ---------- validation ----------
  const validateStep = (currentStep) => {
    const nextErrors = {};
    if (currentStep === 1) {
      if (!selectedProduct) nextErrors.product = "Please select a product";
      if (quantity < 1) nextErrors.quantity = "Quantity must be at least 1";
      if (!unitPrice || unitPrice <= 0)
        nextErrors.unitPrice = "Please enter a valid unit price";
      if (discountPercent < 0 || discountPercent > 100)
        nextErrors.discount = "Discount must be between 0 and 100%";
      if (taxPercent < 0 || taxPercent > 100)
        nextErrors.tax = "Tax must be between 0 and 100%";
    }
    if (currentStep === 2) {
      if (manualAddress) {
        if (!formData.mName?.trim()) nextErrors.mName = "Name is required";
        if (!formData.mMobile?.trim()) nextErrors.mMobile = "Mobile is required";
        if (!formData.mAddress?.trim()) nextErrors.mAddress = "Address is required";
        if (!formData.mCity?.trim()) nextErrors.mCity = "City is required";
        if (!formData.mState?.trim()) nextErrors.mState = "State is required";
        if (!formData.mZip?.trim()) nextErrors.mZip = "PIN code is required";
      } else if (!formData.deliveryAddress) {
        nextErrors.deliveryAddress = "Please select a delivery address";
      }
    }
    if (currentStep === 3) {
      if (!formData.paymentType)
        nextErrors.paymentType = "Please select a payment method";
      if (
        formData.advancePaid === "" ||
        formData.advancePaid === null ||
        formData.advancePaid === undefined
      )
        nextErrors.advancePaid =
          "Please enter the advance amount (0 for full COD)";
      if (advancePaid < 0)
        nextErrors.advancePaid = "Advance paid cannot be negative";
      if (advancePaid > total && total > 0)
        nextErrors.advancePaid = "Advance paid cannot exceed the total amount";
      if (total <= 0) nextErrors.total = "Total amount must be greater than 0";
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const nextStep = async () => {
    if (!validateStep(step)) {
      toast.error("Please fix the highlighted fields before continuing.");
      return;
    }
    // Manual address mode: persist the address first, then use its returned UID
    if (step === 2 && manualAddress) {
      setSavingAddress(true);
      try {
        const res = await postData1(
          "users/add_or_update_address/",
          {
            name: formData.mName.trim(),
            mobile: formData.mMobile.trim(),
            address: formData.mAddress.trim(),
            city: formData.mCity.trim(),
            state: formData.mState.trim(),
            zip_code: formData.mZip.trim(),
            is_default: 1,
          },
          true,
          true
        );
        const newAddrUid = res?.data?.uid || res?.data?.id;
        if (res?.status && newAddrUid) {
          setFormData((prev) => ({
            ...prev,
            deliveryAddress: { uid: newAddrUid },
          }));
          setStep((prev) => prev + 1);
        } else {
          toast.error(res?.message || res?.msg || "Failed to save address.");
        }
      } catch (error) {
        console.error("Error saving address", error);
        toast.error("Failed to save address. Please try again.");
      } finally {
        setSavingAddress(false);
      }
      return;
    }
    setStep((prev) => prev + 1);
  };

  const prevStep = () => setStep((prev) => Math.max(1, prev - 1));

  // console.log(userData)
  // ---------- submit ----------
  const handleSubmit = async () => {
    if (!validateStep(3)) {
      toast.error("Please fix the highlighted fields before submitting.");
      return;
    }
    setSubmitting(true);
    try {
      // Backend Order model contract — see order-management-implementation-plan-updated.md
      const payload = {
        buyer: userData?.uid,
        seller: selectedProduct?.user || selectedProduct?.seller || null,
        product: selectedProduct?.uid,
        quantity,
        asking_price: unitPrice,
        sub_total: subtotal,
        tax: taxAmount, // computed from tax %
        discount: discountAmount, // computed from discount %
        total,
        remain_amount: remainingAmount, // backend field name
        payment_type: formData.paymentType, // 1-10 backend choice
        payment_status: paymentStatus, // 1=Unpaid, 2=Partial, 3=Paid
        order_status: 5,
        delivery_address: formData.deliveryAddress?.uid, // Address UID only
        shipping_equipment: false,
        handling_instalation: false,
        amc_services: false,
        shipping_equipment_amount: 0,
        handling_instalation_amount: 0,
        amc_services_amount: 0,
        others: { payment_mode: "offline",amount_paid:formData.advancePaid, },

      };
      const res = await postData1("product/order/", payload, true, true);
      // if (res?.status_code === 200 || res?.status === true) {
      // console.log("Order creation response", res?.uid);
      const orderId = res?.uid;
      const buyerId = userData?.uid;
      const productId =  selectedProduct?.uid;
      if (res) {
        toast.success("Order created successfully!");
        closeModal();
         await createPaymentRecord(paymentStatus, subtotal, total,buyerId,orderId,productId);
        if (onOrderCreated) onOrderCreated();
      } else {
        toast.error(res?.message || res?.msg || "Failed to create order.");
      }
    } catch (error) {
      console.error("Order creation error", error);
      const serverMsg = error?.response?.data?.message || error?.response?.data?.msg;
      toast.error(serverMsg || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

    const createPaymentRecord = async (paymentStatus, subtotal, total,buyerId, orderId,productId) => {
    try {
      const paymentPayload = {
        order: orderId,
        order_type: 1, // Order
        product: userData?.product_detail?.uid,
        sub_total: subtotal,
        total: formData.advancePaid,
        payment_mode: "offline", // adjust as per your data
        payment_status: paymentStatus, // same integer mapping
        buyer: buyerId,
        product: productId,

        // for mail 
        email : userData?.email,
        product_name: selectedProduct?.equip_name || selectedProduct?.name || "",
        address: formData.deliveryAddress?.address || formData.mAddress || "",
        name: userData?.profile?.first_name ,
        
      };

      const paymentRes = await axios.post(
        `${process.env.REACT_APP_BASEURL}/product/order/payment/`,
        paymentPayload,
        {
          headers: {
            'Authorization': `Token ${localStorage.getItem('adminAuth')}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (paymentRes.status === 201) {
        toast.success("Payment record created successfully!");
      } else {
        console.warn("Payment record creation failed:", paymentRes);
      }
    } catch (error) {
      console.error("Payment creation error:", error);
      // Optionally notify user but don't block the main success
    }
  };

  // ---------- render ----------
  const inputCls = (hasError) =>
    `w-full rounded-xl border px-3.5 py-2.5 text-sm text-slate-800 outline-none transition-all focus:ring-2 ${hasError
      ? "border-red-300 focus:border-red-400 focus:ring-red-100"
      : "border-slate-200 focus:border-teal-500 focus:ring-teal-100"
    }`;

  return (
    <>
      {/* Button to open modal */}
      <button
        onClick={openModal}
        className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-teal-600 to-cyan-600 px-4 py-2.5 text-sm font-semibold text-white shadow-lg shadow-teal-500/25 transition-all hover:-translate-y-0.5 hover:shadow-xl hover:shadow-teal-500/30 active:translate-y-0"
      >
        <ShoppingCart size={16} />
        Create Order
      </button>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 p-3 backdrop-blur-sm sm:p-6">
          <div className="flex max-h-[92vh] w-full max-w-3xl flex-col overflow-hidden rounded-3xl bg-white shadow-2xl">
            {/* Header */}
            <div className="relative bg-gradient-to-r from-slate-800 via-teal-700 to-slate-900 px-6 py-5 text-white sm:px-8">
              <button
                onClick={closeModal}
                className="absolute right-4 top-4 rounded-full bg-white/10 p-1.5 text-white/80 transition hover:bg-white/20 hover:text-white"
              >
                <X size={18} />
              </button>
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-teal-200">
                Offline Order
              </p>
              <h3 className="mt-1 text-xl font-bold sm:text-2xl">
                Create Order
              </h3>
              <p className="mt-1.5 flex items-center gap-1.5 text-sm text-slate-200/90">
                <User size={14} />
                <span className="font-medium text-white">{customerName}</span>
                {userData?.mobile && (
                  <span className="text-slate-300"> · {userData.mobile}</span>
                )}
              </p>
            </div>

            {/* Stepper */}
            <div className="flex items-center gap-2 px-6 pt-5 sm:px-8">
              {STEP_META.map((meta, idx) => {
                const stepNo = idx + 1;
                const active = step === stepNo;
                const done = step > stepNo;
                const Icon = meta.icon;
                return (
                  <div key={meta.title} className="flex items-center gap-2 flex-1">
                    <div className="flex items-center gap-2">
                      <span
                        className={`flex h-8 w-8 items-center justify-center rounded-full transition-colors ${done
                            ? "bg-emerald-500 text-white"
                            : active
                              ? "bg-teal-600 text-white ring-4 ring-teal-100"
                              : "bg-slate-100 text-slate-400"
                          }`}
                      >
                        {done ? (
                          <CheckCircle2 size={16} />
                        ) : (
                          <Icon size={16} />
                        )}
                      </span>
                      <span
                        className={`hidden text-xs font-semibold sm:block ${active ? "text-teal-700" : "text-slate-400"
                          }`}
                      >
                        {meta.title}
                      </span>
                    </div>
                    {stepNo < 3 && (
                      <div
                        className={`h-0.5 flex-1 rounded ${done ? "bg-emerald-400" : "bg-slate-200"
                          }`}
                      ></div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto px-6 py-6 sm:px-8">
              {/* ---------- STEP 1 : Product & Quantity ---------- */}
              {step === 1 && (
                <div className="space-y-6">
                  {/* Product search */}
                  <div className="relative" ref={productDropdownRef}>
                    <label className="mb-1.5 block text-sm font-semibold text-slate-700">
                      Product <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <Search
                        size={16}
                        className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400"
                      />
                      <input
                        type="text"
                        value={formData.productQuery}
                        onChange={(e) => {
                          setField("productQuery", e.target.value);
                          setSelectedProduct(null);
                        }}
                        onFocus={() => setShowProductDropdown(true)}
                        placeholder="Search product by name / brand..."
                        className={`${inputCls(!!errors.product)} pl-10`}
                        autoComplete="off"
                      />
                    </div>
                    {errors.product && (
                      <p className="mt-1 text-xs text-red-500">{errors.product}</p>
                    )}

                    {showProductDropdown && (
                      <div className="absolute z-50 mt-2 max-h-72 w-full overflow-y-auto rounded-2xl border border-slate-200 bg-white shadow-xl shadow-slate-900/10">
                        {loadingProducts && (
                          <div className="flex items-center gap-2 px-4 py-3 text-sm text-slate-500">
                            <Loader2 size={14} className="animate-spin" />
                            Searching...
                          </div>
                        )}
                        {!loadingProducts && productSuggestions.length === 0 && (
                          <div className="flex items-center gap-2 px-4 py-3 text-sm text-slate-400">
                            <Package size={14} />
                            {formData.productQuery
                              ? "No products found."
                              : "Start typing to search products."}
                          </div>
                        )}
                        {productSuggestions.slice(0, 12).map((product) => (
                          <button
                            key={
                              product?.uid ||
                              product?.id ||
                              product?.product_id
                            }
                            type="button"
                            onClick={() => selectProduct(product)}
                            className="flex w-full items-center gap-3 border-b border-slate-50 px-4 py-2.5 text-left transition hover:bg-teal-50 last:border-0"
                          >
                            <span className="flex h-11 w-11 flex-shrink-0 items-center justify-center overflow-hidden rounded-lg border border-slate-100 bg-slate-50">
                              {productThumb(product) ? (
                                <img
                                  src={productThumb(product)}
                                  alt=""
                                  className="h-full w-full object-cover"
                                />
                              ) : (
                                <Package size={18} className="text-slate-300" />
                              )}
                            </span>
                            <span className="min-w-0 flex-1">
                              <span className="block truncate text-sm font-semibold text-slate-800">
                                {product?.equip_name ||
                                  product?.name ||
                                  product?.title ||
                                  "Product"}
                              </span>
                              {product?.brand && (
                                <span className="block text-xs text-slate-500">
                                  {product.brand}
                                </span>
                              )}
                            </span>
                            {(product?.asking_price || product?.price) && (
                              <span className="whitespace-nowrap text-sm font-bold text-slate-700">
                                <IndianRupee
                                  size={12}
                                  className="mr-0.5 inline"
                                />
                                {Number(
                                  product?.asking_price || product?.price
                                ).toLocaleString("en-IN")}
                              </span>
                            )}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Quantity + Unit price + Discount + Tax */}
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                    <div>
                      <label className="mb-1.5 block text-sm font-semibold text-slate-700">
                        Quantity <span className="text-red-500">*</span>
                      </label>
                      <div className="flex items-center rounded-xl border border-slate-200 focus-within:ring-2 focus-within:ring-teal-100">
                        <button
                          type="button"
                          onClick={() =>
                            setField(
                              "quantity",
                              Math.max(1, (parseInt(formData.quantity, 10) || 1) - 1)
                            )
                          }
                          className="px-3 py-2.5 text-slate-500 transition hover:text-teal-600"
                        >
                          <Minus size={14} />
                        </button>
                        <input
                          type="number"
                          name="quantity"
                          min="1"
                          value={formData.quantity}
                          onChange={(e) => setField("quantity", e.target.value)}
                          className="w-full border-0 bg-transparent py-2.5 text-center text-sm font-semibold text-slate-800 outline-none"
                        />
                        <button
                          type="button"
                          onClick={() =>
                            setField(
                              "quantity",
                              (parseInt(formData.quantity, 10) || 1) + 1
                            )
                          }
                          className="px-3 py-2.5 text-slate-500 transition hover:text-teal-600"
                        >
                          <Plus size={14} />
                        </button>
                      </div>
                      {errors.quantity && (
                        <p className="mt-1 text-xs text-red-500">
                          {errors.quantity}
                        </p>
                      )}
                    </div>
                    <div>
                      <label className="mb-1.5 block text-sm font-semibold text-slate-700">
                        Unit Price ({'\u20B9'}){" "}
                        <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="number"
                        name="unitPrice"
                        min="0"
                        value={formData.unitPrice}
                        onChange={(e) => setField("unitPrice", e.target.value)}
                        placeholder="0.00"
                        className={inputCls(!!errors.unitPrice)}
                      />
                      {errors.unitPrice && (
                        <p className="mt-1 text-xs text-red-500">
                          {errors.unitPrice}
                        </p>
                      )}
                    </div>
                    <div>
                      <label className="mb-1.5 block text-sm font-semibold text-slate-700">
                        Discount ({"%"})
                      </label>
                      <input
                        type="number"
                        name="discount"
                        min="0"
                        max="100"
                        value={formData.discount}
                        onChange={(e) => setField("discount", e.target.value)}
                        placeholder="0"
                        className={inputCls(!!errors.discount)}
                      />
                      {errors.discount && (
                        <p className="mt-1 text-xs text-red-500">
                          {errors.discount}
                        </p>
                      )}
                    </div>
                    <div>
                      <label className="mb-1.5 block text-sm font-semibold text-slate-700">
                        Tax ({"%"})
                      </label>
                      <input
                        type="number"
                        name="tax"
                        min="0"
                        max="100"
                        value={formData.tax}
                        onChange={(e) => setField("tax", e.target.value)}
                        placeholder="0"
                        className={inputCls(!!errors.tax)}
                      />
                      {errors.tax && (
                        <p className="mt-1 text-xs text-red-500">{errors.tax}</p>
                      )}
                    </div>
                  </div>

                  {/* Live subtotal preview */}
                  <div className="rounded-2xl border border-teal-100 bg-teal-50/60 p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-teal-600 text-white">
                          <Package size={18} />
                        </span>
                        <div>
                          <p className="text-sm font-semibold text-slate-800">
                            {selectedProduct?.equip_name || "No product selected"}
                          </p>
                          <p className="text-xs text-slate-500">
                            {quantity || 0} × {"\u20B9"}
                            {unitPrice.toLocaleString("en-IN", {
                              maximumFractionDigits: 2,
                            })}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-slate-500">Sub Total</p>
                        <p className="font-bold text-slate-800">
                          {"\u20B9"}
                          {subtotal.toLocaleString("en-IN", {
                            maximumFractionDigits: 2,
                          })}
                        </p>
                      </div>
                    </div>
                    {(taxPercent > 0 || discountPercent > 0) && (
                      <div className="mt-3 flex items-center justify-between rounded-xl bg-white/70 px-3 py-2 text-xs">
                        <div className="space-y-0.5 text-slate-500">
                          {taxPercent > 0 && (
                            <p>+ Tax ({taxPercent}%)</p>
                          )}
                          {discountPercent > 0 && (
                            <p>- Discount ({discountPercent}%)</p>
                          )}
                        </div>
                        <p className="text-base font-bold text-teal-700">
                          {"\u20B9"}
                          {total.toLocaleString("en-IN", {
                            maximumFractionDigits: 2,
                          })}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* ---------- STEP 2 : Delivery Address ---------- */}
              {step === 2 && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <label className="block text-sm font-semibold text-slate-700">
                      Delivery Address <span className="text-red-500">*</span>
                    </label>
                    {/* Mode toggle */}
                    {!loadingAddresses && (
                      <div className="flex rounded-xl border border-slate-200 bg-slate-50 p-1">
                        <button
                          type="button"
                          onClick={() => setManualAddress(false)}
                          className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition ${!manualAddress
                              ? "bg-white text-teal-700 shadow-sm"
                              : "text-slate-500 hover:text-slate-700"
                            }`}
                        >
                          Saved Address
                        </button>
                        <button
                          type="button"
                          onClick={() => setManualAddress(true)}
                          className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition ${manualAddress
                              ? "bg-white text-teal-700 shadow-sm"
                              : "text-slate-500 hover:text-slate-700"
                            }`}
                        >
                          New Address
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Manual address form */}
                  {manualAddress ? (
                    <div className="space-y-4 rounded-2xl border border-slate-200 bg-slate-50/50 p-4">
                      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <div>
                          <label className="mb-1.5 block text-xs font-semibold text-slate-600">
                            Recipient Name <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            value={formData.mName}
                            onChange={(e) => setField("mName", e.target.value)}
                            placeholder="Full name"
                            className={inputCls(!!errors.mName)}
                          />
                          {errors.mName && (
                            <p className="mt-1 text-xs text-red-500">{errors.mName}</p>
                          )}
                        </div>
                        <div>
                          <label className="mb-1.5 block text-xs font-semibold text-slate-600">
                            Mobile <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="tel"
                            value={formData.mMobile}
                            onChange={(e) => setField("mMobile", e.target.value)}
                            placeholder="10-digit mobile"
                            className={inputCls(!!errors.mMobile)}
                          />
                          {errors.mMobile && (
                            <p className="mt-1 text-xs text-red-500">{errors.mMobile}</p>
                          )}
                        </div>
                      </div>
                      <div>
                        <label className="mb-1.5 block text-xs font-semibold text-slate-600">
                          Full Address <span className="text-red-500">*</span>
                        </label>
                        <textarea
                          value={formData.mAddress}
                          onChange={(e) => setField("mAddress", e.target.value)}
                          placeholder="House no, street, area..."
                          rows={2}
                          className={`${inputCls(!!errors.mAddress)} resize-none`}
                        />
                        {errors.mAddress && (
                          <p className="mt-1 text-xs text-red-500">{errors.mAddress}</p>
                        )}
                      </div>
                      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                        <div>
                          <label className="mb-1.5 block text-xs font-semibold text-slate-600">
                            City <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            value={formData.mCity}
                            onChange={(e) => setField("mCity", e.target.value)}
                            placeholder="City / district"
                            className={inputCls(!!errors.mCity)}
                          />
                          {errors.mCity && (
                            <p className="mt-1 text-xs text-red-500">{errors.mCity}</p>
                          )}
                        </div>
                        <div>
                          <label className="mb-1.5 block text-xs font-semibold text-slate-600">
                            State <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            value={formData.mState}
                            onChange={(e) => setField("mState", e.target.value)}
                            placeholder="State"
                            className={inputCls(!!errors.mState)}
                          />
                          {errors.mState && (
                            <p className="mt-1 text-xs text-red-500">{errors.mState}</p>
                          )}
                        </div>
                        <div>
                          <label className="mb-1.5 block text-xs font-semibold text-slate-600">
                            PIN Code <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            value={formData.mZip}
                            onChange={(e) => setField("mZip", e.target.value)}
                            placeholder="PIN code"
                            className={inputCls(!!errors.mZip)}
                          />
                          {errors.mZip && (
                            <p className="mt-1 text-xs text-red-500">{errors.mZip}</p>
                          )}
                        </div>
                      </div>
                      <p className="text-xs text-slate-500">
                        The new address will be saved to the customer's profile
                        and used for this order.
                      </p>
                    </div>
                  ) : (
                    <>
                      {loadingAddresses && (
                        <div className="flex items-center gap-2 py-6 text-sm text-slate-500">
                          <Loader2 size={16} className="animate-spin" />
                          Loading saved addresses...
                        </div>
                      )}
                      {!loadingAddresses && addressList.length === 0 && (
                        <div className="flex items-start gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-700">
                          <MapPin size={18} className="mt-0.5 flex-shrink-0" />
                          <p>
                            No saved address found for this customer. Switch to
                            "New Address" above to add one.
                          </p>
                        </div>
                      )}
                    </>
                  )}

                  {/* Saved address cards */}
                  {!manualAddress && (
                    <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
                      {addressList.map((address, index) => {
                        const selected =
                          !!formData.deliveryAddress &&
                          (formData.deliveryAddress.uid
                            ? formData.deliveryAddress.uid === address?.uid
                            : formData.deliveryAddress === address);
                        const addressLine = [
                          address?.address,
                          address?.landmark,
                          `${[address?.city, address?.state].filter(Boolean).join(", ")
                          }${address?.zip_code ? ` - ${address.zip_code}` : ""}`,
                        ]
                          .filter(Boolean)
                          .join(", ");
                        return (
                          <button
                            type="button"
                            key={address?.uid || index}
                            onClick={() => setField("deliveryAddress", address)}
                            className={`w-full rounded-2xl border p-4 text-left transition-all ${selected
                                ? "border-teal-500 bg-teal-50/60 ring-2 ring-teal-100"
                                : "border-slate-200 hover:border-teal-300 hover:bg-slate-50"
                              }`}
                          >
                            <div className="flex items-start gap-3">
                              <span
                                className={`mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full border-2 ${selected
                                    ? "border-teal-600"
                                    : "border-slate-300"
                                  }`}
                              >
                                {selected && (
                                  <span className="h-2.5 w-2.5 rounded-full bg-teal-600"></span>
                                )}
                              </span>
                              <div className="min-w-0">
                                <p className="text-sm font-semibold text-slate-800">
                                  {address?.name ||
                                    userData?.profile?.first_name ||
                                    `Address ${index + 1}`}
                                  {String(address?.is_default) === "1" && (
                                    <span className="ml-2 inline-block rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-600 border border-emerald-200">
                                      Default
                                    </span>
                                  )}
                                </p>
                                <p className="mt-0.5 text-sm text-slate-600">
                                  {addressLine}
                                </p>
                                {address?.mobile && (
                                  <p className="mt-0.5 text-xs text-slate-400">
                                    {address.mobile}
                                  </p>
                                )}
                              </div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                  {errors.deliveryAddress && (
                    <p className="mt-2 text-xs text-red-500">
                      {errors.deliveryAddress}
                    </p>
                  )}
                </div>
              )}

              {/* ---------- STEP 3 : Payment & Summary ---------- */}
              {step === 3 && (
                <div className="grid grid-cols-1 gap-6 lg:grid-cols-5">
                  {/* Summary */}
                  <div className="lg:col-span-2">
                    <div className="overflow-hidden rounded-2xl border border-slate-200">
                      <div className="bg-slate-50 px-4 py-3">
                        <p className="text-sm font-bold text-slate-700">
                          Order Summary
                        </p>
                      </div>
                      <div className="space-y-2.5 px-4 py-4 text-sm">
                        <div className="flex justify-between gap-3">
                          <span className="text-slate-500">Product</span>
                          <span className="text-right font-semibold text-slate-800">
                            {selectedProduct?.equip_name || "\u2014"}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-500">Quantity</span>
                          <span className="font-semibold text-slate-800">
                            {quantity}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-500">Sub Total</span>
                          <span className="font-semibold text-slate-800">
                            {"\u20B9"}
                            {subtotal.toLocaleString("en-IN", {
                              maximumFractionDigits: 2,
                            })}
                          </span>
                        </div>
                        {taxPercent > 0 && (
                          <div className="flex justify-between">
                            <span className="text-slate-500">
                              Tax ({taxPercent}%)
                            </span>
                            <span className="font-semibold text-slate-800">
                              + {"\u20B9"}
                              {taxAmount.toLocaleString("en-IN", {
                                maximumFractionDigits: 2,
                              })}
                            </span>
                          </div>
                        )}
                        {discountPercent > 0 && (
                          <div className="flex justify-between">
                            <span className="text-slate-500">
                              Discount ({discountPercent}%)
                            </span>
                            <span className="font-semibold text-emerald-600">
                              - {"\u20B9"}
                              {discountAmount.toLocaleString("en-IN", {
                                maximumFractionDigits: 2,
                              })}
                            </span>
                          </div>
                        )}
                        <div className="flex justify-between border-t border-slate-200 pt-2.5">
                          <span className="font-bold text-slate-800">
                            Total
                          </span>
                          <span className="font-bold text-teal-700">
                            {"\u20B9"}
                            {total.toLocaleString("en-IN", {
                              maximumFractionDigits: 2,
                            })}
                          </span>
                        </div>
                        {errors.total && (
                          <p className="text-xs text-red-500">
                            {errors.total}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Payment */}
                  <div className="space-y-5 lg:col-span-3">
                    {/* Advance payment */}
                    <div>
                      <label className="mb-1.5 block text-sm font-semibold text-slate-700">
                        Advance Paid (received over call){" "}
                        <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <IndianRupee
                          size={16}
                          className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400"
                        />
                        <input
                          type="number"
                          name="advancePaid"
                          min="0"
                          value={formData.advancePaid}
                          onChange={(e) =>
                            setField("advancePaid", e.target.value)
                          }
                          placeholder="0.00"
                          className={`${inputCls(
                            !!errors.advancePaid
                          )} pl-9`}
                        />
                      </div>
                      {errors.advancePaid && (
                        <p className="mt-1 text-xs text-red-500">
                          {errors.advancePaid}
                        </p>
                      )}
                      <p className="mt-1.5 text-xs text-slate-500">
                        Remaining to collect:{" "}
                        <span className="font-bold text-slate-700">
                          {"\u20B9"}
                          {remainingAmount.toLocaleString("en-IN", {
                            maximumFractionDigits: 2,
                          })}
                        </span>
                      </p>
                    </div>

                    {/* Remaining payment method */}
                    <div>
                      <label className="mb-2 block text-sm font-semibold text-slate-700">
                        Remaining Payment Method{" "}
                        <span className="text-red-500">*</span>
                      </label>
                      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                        {PAYMENT_METHODS.map((method) => {
                          const selected =
                            formData.paymentType === method.value;
                          const Icon = method.icon;
                          return (
                            <button
                              type="button"
                              key={method.value}
                              onClick={() =>
                                setField("paymentType", method.value)
                              }
                              className={`rounded-2xl border p-3.5 text-left transition-all ${selected
                                  ? "border-teal-500 bg-teal-50/60 ring-2 ring-teal-100"
                                  : "border-slate-200 hover:border-teal-300 hover:bg-slate-50"
                                }`}
                            >
                              <Icon
                                size={20}
                                className={
                                  selected
                                    ? "text-teal-600"
                                    : "text-slate-400"
                                }
                              />
                              <p className="mt-2 text-sm font-bold text-slate-800">
                                {method.label}
                              </p>
                              <p className="mt-0.5 text-[11px] leading-snug text-slate-500">
                                {method.hint}
                              </p>
                            </button>
                          );
                        })}
                      </div>
                      {errors.paymentType && (
                        <p className="mt-1 text-xs text-red-500">
                          {errors.paymentType}
                        </p>
                      )}
                    </div>

                    {/* Payment status preview */}
                    <div className="flex items-center justify-between rounded-2xl border border-slate-200 bg-slate-50 p-3.5 text-sm">
                      <span className="font-medium text-slate-600">
                        Payment status
                      </span>
                      <span
                        className={`rounded-full px-3 py-1 text-xs font-bold ${PAYMENT_STATUS[paymentStatus]?.badge
                          }`}
                      >
                        {PAYMENT_STATUS[paymentStatus]?.label}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Sticky footer */}
            <div className="flex items-center justify-between border-t border-slate-100 bg-white/90 px-6 py-4 backdrop-blur sm:px-8">
              {step > 1 ? (
                <button
                  type="button"
                  onClick={prevStep}
                  className="inline-flex items-center gap-1.5 rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
                >
                  <ChevronLeft size={16} />
                  Back
                </button>
              ) : (
                <span></span>
              )}

              {step < 3 ? (
                <button
                  type="button"
                  onClick={nextStep}
                  disabled={savingAddress}
                  className="inline-flex items-center gap-1.5 rounded-xl bg-gradient-to-r from-teal-600 to-cyan-600 px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-teal-500/25 transition hover:-translate-y-0.5 hover:shadow-xl active:translate-y-0 disabled:translate-y-0 disabled:opacity-60"
                >
                  {savingAddress ? (
                    <>
                      <Loader2 size={16} className="animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      Next
                      <ChevronRight size={16} />
                    </>
                  )}
                </button>
              ) : (
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={() => handleSubmit()}
                    disabled={submitting}
                    className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-teal-600 to-cyan-600 px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-teal-500/25 transition hover:-translate-y-0.5 hover:shadow-xl disabled:translate-y-0 disabled:opacity-60"
                  >
                    {submitting ? (
                      <>
                        <Loader2 size={16} className="animate-spin" />
                        Creating...
                      </>
                    ) : (
                      <>
                        <CheckCircle2 size={16} />
                        Create Order
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <ToastContainer />
    </>
  );
};

export default CreateOrderModal;
